import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentRestaurant, getPreparedMenu } from '../../../selectors';
import { prepareMenu, sendTestOrder } from '../../../actions';
import { List, Checkbox, Card, Button, Input } from 'semantic-ui-react';
import i18next from 'i18next';
import { formatPrice } from '../../../helpers/format';
import { randomNumber } from '../../../helpers/random';

import './TestOrder.scss';

const TestOrder = (props) => {
    const { history } = props;
    const restaurant = useSelector(getCurrentRestaurant);
    const isRtl = i18next.dir() === 'rtl';
    const initialOrder = {
        cart: {
            cartId: randomNumber(5),
            creationDate: new Date(),
            orderType: 'sitting',
            remarks: i18next.t('testOrder.defaultRemarks'),
            items: {}
        },
        checkout: {
            user: { name: i18next.t('testOrder.defaultUser') },
            restaurantId: restaurant ? restaurant.id : '',
            subtotal: 0
        },
        shouldSendPayments: false
    };

    const [order, setOrder] = useState(initialOrder);
    const [cart, setCart] = useState({});
    const menu = useSelector(getPreparedMenu);

    const dispatch = useDispatch();

    useEffect(() => {
        if (!restaurant) {
            return history.push('/admin/restaurants');
        }

        if (restaurant.menuId && !menu.menuId) {
            dispatch(prepareMenu(restaurant.menuId, restaurant.id, restaurant.timezone));
        }
    }, [restaurant, menu]);

    const renderCategoryTree = (category, parentItemId = null) => {
        const childCategories = menu.categories.filter(cat => cat.primaryParentId === category.id);

        return (
            <List.Item key={category.id}>
                <span className="category-name">{category.name}</span>
                <List>
                    {childCategories && childCategories.length > 0
                        ? childCategories.map(cat => renderCategoryTree(cat))
                        : category.itemsInstances.map(itemInstance => renderListItem(itemInstance, parentItemId, category.id))
                    }
                </List>
            </List.Item>
        );
    };

    const $itemId = (id, parentId) => `${parentId ? `${parentId}:` : ''}${id}`;

    const renderListItem = (itemInstance, parentItemId = null, parentCatId = null) => {
        const handleItemChecked = (data, parentItemId) => {
            const itemId = $itemId(data.value, parentItemId);

            if (cart[itemId]) {
                const newCart = { ...cart };

                Object.keys(cart).forEach(key => {
                    key.includes(data.value) && delete newCart[key];
                });

                setCart(newCart);
            } else {
                setCart({ ...cart, [itemId]: { id: data.value, parentItemId, parentCatId } });
            }
        };

        const item = menu.items[itemInstance.id];

        return (
            <List.Item key={item.id} style={{ marginBottom: 5 }}>
                <Checkbox
                    label={item.name}
                    value={item.id}
                    checked={!!cart[$itemId(item.id, parentItemId)]}
                    onChange={(e, data) => handleItemChecked(data, parentItemId)} />
                {cart[$itemId(item.id, parentItemId)] && item.subcategoriesIds.length > 0 &&
                    <List>
                        {item.subcategoriesIds
                            .map(id => menu.categories.find(cat => cat.id === id))
                            .map(subCategory => renderCategoryTree(subCategory, item.id))
                        }
                    </List>}
            </List.Item>
        );
    };

    const renderOrderTypeButton = type => {
        return (
            <Button
                icon
                color={order.cart.orderType === type ? 'blue' : undefined}
                onClick={() => setOrder({ ...order, cart: { ...order.cart, orderType: type } })}
            >
                {i18next.t(type)}
            </Button>
        );
    };

    const buildItemHirarchy = item => {
        let selections = {};

        Object.values(cart).filter(subItem => subItem.parentItemId === item.id).forEach(subItem => {
            selections = {
                ...selections,
                [subItem.parentCatId]: selections[subItem.parentCatId] ? selections[subItem.parentCatId].concat(buildItemHirarchy(subItem)) : [buildItemHirarchy(subItem)]
            };
        });

        return {
            id: item.id,
            selections
        };
    };

    const prepareOrder = () => {
        const subtotal = Object.values(cart).reduce((total, item) => {
            return total += menu.items[item.id].price;
        }, 0);

        return {
            ...order,
            checkout: {
                ...order.checkout,
                subtotal
            },
            cart: {
                ...order.cart,
                items: Object.values(cart).filter(item => item.parentItemId === null).map(buildItemHirarchy)
            }
        };
    };

    return (
        <div className="pi-test-order">
            <div className="order-details-menu-container">
                <Card style={{ flex: 1, width: '100%' }}>
                    <Card.Header className="card-header">
                        {i18next.t('testOrder.orderDetails')}:
                    </Card.Header>
                    <Card.Content>
                        <div className="main-actions-row">
                            <Button.Group>
                                {renderOrderTypeButton('sitting')}
                                {renderOrderTypeButton('takeaway')}
                            </Button.Group>
                            <Input label={i18next.t('testOrder.clientName')} placeholder={i18next.t('testOrder.clientName')} value={order.checkout.user.name} onChange={(e, { value }) => setOrder({ ...order, checkout: { ...order.checkout, user: { name: value } } })} />
                            <Input label={i18next.t('testOrder.orderId')} placeholder={i18next.t('testOrder.orderId')} value={order.cart.cartId} onChange={(e, { value }) => setOrder({ ...order, cart: { ...order.cart, cartId: value } })} />
                        </div>
                        <Input fluid label={i18next.t('testOrder.remarks')} placeholder={i18next.t('testOrder.remarks')} value={order.cart.remarks} onChange={(e, { value }) => setOrder({ ...order, cart: { ...order.cart, remarks: value } })} />
                        <div className="send-payment">
                            <Checkbox label={i18next.t('testOrder.sendPayment')} onChange={(e, { checked }) => setOrder({ ...order, shouldSendPayments: checked })} />
                        </div>
                    </Card.Content>
                </Card>

                <Card style={{ width: '100%', margin: 0 }}>
                    <Card.Header className="card-header">
                        {i18next.t('testOrder.chooseItems')}
                    </Card.Header>
                    <Card.Content>
                        <List>
                            {menu.categories &&
                                menu.categories
                                    .filter(cat => cat.primaryParentId === null && cat.type === 'primary')
                                    .map(cat => renderCategoryTree(cat))}
                        </List>
                    </Card.Content>
                </Card>
            </div>
            <Card className="cart" style={{ left: isRtl ? 10 : null, right: isRtl ? null : 10 }}>
                <Card.Header className="card-header">
                    {i18next.t('testOrder.selectedItems')}
                </Card.Header>
                <Card.Content style={{ overflowY: 'auto' }}>
                    {cart && Object.keys(cart).sort((itemA, itemB) => itemA < itemB ? -1 : itemA > itemB ? 1 : 0).map((cartItemId, i) => {
                        const cartItem = cart[cartItemId];
                        const item = menu.items[cartItem.id];

                        return (
                            <div key={i}>
                                <Card className={`cart-item ${cartItem.parentItemId ? 'child' : ''}`}>
                                    <span style={{ width: '80%' }}>{item.name}</span>
                                    <span style={{ width: '20%', textAlign: 'center' }}>{formatPrice(item.price, restaurant.currencyCode)}</span>
                                </Card>
                            </div>
                        );
                    })}
                </Card.Content>
                <Card.Content extra style={{ display: 'flex' }}>
                    <Button fluid positive onClick={() => dispatch(sendTestOrder(prepareOrder()))}>
                        {i18next.t('testOrder.sendOrder')}
                    </Button>
                    <Button fluid negative onClick={() => {
                        setCart({});
                    }}>
                        {i18next.t('testOrder.cleanCart')}
                    </Button>
                </Card.Content>
            </Card>

        </div>
    );
};

TestOrder.propTypes = {
    history: PropTypes.object
};

export default TestOrder;